import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GlobalOutlined, ExportOutlined, CopyOutlined, BulbOutlined } from "@ant-design/icons";
import { FaFacebook, FaInstagram, FaXTwitter, FaWhatsapp } from "react-icons/fa6";
import { MdOutlineEmail } from "react-icons/md";
import { useUser } from "../../../contexts/UserContext/UserContext";
import { useToast } from "../../../contexts/ToastContext";
import getBookingSiteUrl from "../../dashboard/components/bookingSiteUrl";
import { markBookingSiteShared } from "../../dashboard/components/gettingStartedStorage";
import QrCodePanel from "./QrCodePanel";
import MarketingFlyerCard from "./MarketingFlyerCard";

const ShareGrow = () => {
    const { t } = useTranslation("shareGrow");
    const { state, myCompanyData } = useUser();
    const toast = useToast();

    const userId = state?.user?.id;
    const bookingUrl = getBookingSiteUrl(myCompanyData?.id);
    const companyName = myCompanyData?.name;
    const logoUrl = myCompanyData?.logo?.light || myCompanyData?.logo?.dark;

    const caption = useMemo(() => {
        if (!bookingUrl) return "";
        return companyName
            ? t("captionWithCompany", { company: companyName, url: bookingUrl })
            : t("captionGeneric", { url: bookingUrl });
    }, [bookingUrl, companyName, t]);

    const markShared = () => markBookingSiteShared(userId);

    const handleCopyLink = async () => {
        if (!bookingUrl) return;

        try {
            await navigator.clipboard.writeText(bookingUrl);
            toast.success({ message: t("linkCopied") });
        } catch (_error) {
            /* clipboard may be unavailable, ignore */
        }

        markShared();
    };

    const handlePreviewSite = () => {
        if (!bookingUrl) return;
        window.open(bookingUrl, "_blank", "noopener,noreferrer");
    };

    const handleCopyCaption = async (isInstagram = false) => {
        if (!caption) return;

        try {
            await navigator.clipboard.writeText(caption);
            toast.success({ message: isInstagram ? t("instagramCaptionCopied") : t("captionCopied") });
        } catch (_error) {
            /* clipboard may be unavailable, ignore */
        }
    };

    const openShareWindow = (url) => {
        window.open(url, "_blank", "noopener,noreferrer");
        markShared();
    };

    const handleInstagramShare = async () => {
        await handleCopyCaption(true);
        window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
        markShared();
    };

    const handleEmailShare = () => {
        const subject = encodeURIComponent(t("emailSubject"));
        const body = encodeURIComponent(caption);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
        markShared();
    };

    const shareChannels = [
        {
            key: "facebook",
            label: t("facebook"),
            icon: <FaFacebook />,
            iconClassName: "text-[#1877F2]",
            onClick: () =>
                openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(bookingUrl)}`),
        },
        {
            key: "instagram",
            label: t("instagram"),
            icon: <FaInstagram />,
            iconClassName: "text-[#E1306C]",
            onClick: handleInstagramShare,
        },
        {
            key: "x",
            label: "X",
            icon: <FaXTwitter />,
            iconClassName: "text-gray-900",
            onClick: () => openShareWindow(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`),
        },
        {
            key: "whatsapp",
            label: t("whatsapp"),
            icon: <FaWhatsapp />,
            iconClassName: "text-[#25D366]",
            onClick: () => openShareWindow(`https://wa.me/?text=${encodeURIComponent(caption)}`),
        },
        {
            key: "email",
            label: t("email"),
            icon: <MdOutlineEmail />,
            iconClassName: "text-gray-500",
            onClick: handleEmailShare,
        },
    ];

    return (
        <div className="min-w-0">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 sm:text-xl">{t("title")}</h2>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-3">
                <div className="flex min-w-0 flex-col gap-4 lg:col-span-2 lg:gap-3">
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
                        <h3 className="text-left text-base font-semibold text-gray-900">{t("yourBookingWebsite")}</h3>

                        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-700 sm:px-4">
                                <GlobalOutlined className="shrink-0 text-gray-400" />
                                <span className="min-w-0 break-all sm:truncate">{bookingUrl || t("noBookingSite")}</span>
                            </div>
                            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:shrink-0 sm:gap-3">
                                <button
                                    type="button"
                                    onClick={handlePreviewSite}
                                    disabled={!bookingUrl}
                                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:gap-3">
                                    <ExportOutlined /> {t("previewMySite")}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCopyLink}
                                    disabled={!bookingUrl}
                                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#5D5DF6] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#4a4af0] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:gap-3">
                                    <CopyOutlined /> {t("copyLink")}
                                </button>
                            </div>
                        </div>

                        <p className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-gray-400">
                            {t("shareTo")}
                        </p>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-5">
                            {shareChannels.map((channel) => (
                                <button
                                    key={channel.key}
                                    type="button"
                                    onClick={channel.onClick}
                                    disabled={!bookingUrl}
                                    className="flex min-w-0 flex-col items-center gap-2 rounded-xl border border-gray-200 px-2 py-3 text-xs font-medium text-gray-700 transition-colors hover:border-[#5D5DF6] hover:bg-[#F5F5FF] disabled:cursor-not-allowed disabled:opacity-50 sm:gap-3 sm:py-4 sm:text-sm">
                                    <span className={`text-lg sm:text-xl ${channel.iconClassName}`}>{channel.icon}</span>
                                    <span className="text-center leading-tight">{channel.label}</span>
                                </button>
                            ))}
                        </div>

                        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4">
                            <p className="break-words text-sm text-gray-700">{caption}</p>
                            <button
                                type="button"
                                onClick={() => handleCopyCaption(false)}
                                disabled={!caption}
                                className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-[#5D5DF6] disabled:cursor-not-allowed disabled:opacity-50 sm:gap-3">
                                <CopyOutlined /> {t("copyCaption")}
                            </button>
                        </div>
                    </div>

                    <MarketingFlyerCard bookingUrl={bookingUrl} companyName={companyName} logoUrl={logoUrl} />
                </div>

                <div className="flex min-w-0 flex-col gap-4 lg:gap-3">
                    <QrCodePanel url={bookingUrl} />

                    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
                        <p className="flex items-center gap-3 text-base font-semibold text-gray-900">
                            <BulbOutlined className="text-[#5D5DF6]" /> {t("tipsTitle")}
                        </p>
                        <ol className="mt-3 flex flex-col gap-3">
                            {[1, 2, 3].map((step) => (
                                <li key={step} className="flex items-start gap-3 text-sm text-gray-600">
                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F0F0FE] text-xs font-semibold text-[#5D5DF6]">
                                        {step}
                                    </span>
                                    {t(`tip${step}`)}
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareGrow;
